import { useEffect, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Kbd, Loading } from '../../ui/kit.tsx';
import { SearchBox } from '../../ui/SearchBox.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { APPROVAL_LEVEL_LABELS, SUB_AFFILIATE_LABELS, type ApprovalLevel, type NetworkList, type NetworkRow, type SubAffiliatePolicy } from './types.ts';

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export function Networks() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const q = query.get('q') ?? '';
  const list = useOp<NetworkList>('admin.networks.list', { q: q || undefined }, changes);
  const rows = list.data?.rows ?? [];
  const [cursor, setCursor] = useState(0);
  const editing = query.get('network');
  const open = (ref: string) => setQuery({ network: ref });
  useEffect(() => setCursor(0), [q]);

  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      o: () => rows[cursor] && open(rows[cursor]!.ref),
      enter: () => rows[cursor] && open(rows[cursor]!.ref),
      n: () => list.data?.canManage && open('new'),
    },
    !editing,
  );

  const current = editing === 'new' ? null : rows.find((r) => r.ref === editing);
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Networks</h1>
          <p className="muted">Affiliate networks, how their links are built, and which of your sites still lack an ad space.</p>
        </div>
        {list.data?.canManage ? (
          <button type="button" className="btn btn-primary" onClick={() => open('new')}>
            New network <Kbd>n</Kbd>
          </button>
        ) : null}
      </header>
      <div className="toolbar">
        <SearchBox value={q} onChange={(value) => setQuery({ q: value || null }, { replace: true })} placeholder="Search networks" />
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No networks match.</Empty> : null}
      {rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Network</th>
              <th scope="col">Link builder</th>
              <th scope="col">Click ref</th>
              <th scope="col">Countries</th>
              <th scope="col" className="num">Programs</th>
              <th scope="col">Ad space missing on</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.ref} className={index === cursor ? 'cursor' : undefined} onClick={() => open(row.ref)} onMouseEnter={() => setCursor(index)}>
                <td>
                  <span className="strong">{row.name}</span> {row.status !== 'active' ? <span className="badge badge-muted">inactive</span> : null}
                  <div className="muted small">{row.slug}</div>
                </td>
                <td>{row.generatorKey ? <span title={row.generatorLabel ?? ''}>{row.generatorKey}</span> : <span className="text-warn">none</span>}</td>
                <td className="mono">{row.clickRefParameter ?? <span className="text-warn">none</span>}</td>
                <td>{row.countries.join(', ') || <span className="muted">—</span>}</td>
                <td className="num">
                  {row.approvedPrograms}/{row.programs}
                </td>
                <td>
                  {row.sitesWithoutAdSpace.length ? (
                    <span className="text-warn">{row.sitesWithoutAdSpace.map((s) => s.name).join(', ')}</span>
                  ) : (
                    <span className="text-ok">all sites set</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {list.data && editing && (editing === 'new' || current) ? (
        <NetworkDrawer key={editing} data={list.data} network={current ?? null} onClose={() => setQuery({ network: null })} onSaved={(ref) => setQuery({ network: ref }, { replace: true })} />
      ) : null}
    </section>
  );
}

function NetworkDrawer({ data, network, onClose, onSaved }: { data: NetworkList; network: NetworkRow | null; onClose: () => void; onSaved: (ref: string) => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState(() => ({
    name: network?.name ?? '',
    slug: network?.slug ?? '',
    kind: network?.kind ?? 'affiliate_network',
    status: network?.status ?? 'active',
    homepageUrl: network?.homepageUrl ?? '',
    generatorKey: network?.generatorKey ?? 'base',
    clickoutAdapter: network?.clickoutAdapter ?? 'standard',
    countryCodes: network?.countries ?? [],
    clickRefParameter: network?.clickRefParameter ?? '',
    validateSubid: network?.validateSubid ?? false,
    validationRegex: network?.validationRegex ?? '',
    approvalLevel: network?.approvalLevel ?? ('account' as ApprovalLevel),
    subAffiliatePolicy: network?.subAffiliatePolicy ?? ('allowed' as SubAffiliatePolicy),
  }));
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const readOnly = !data.canManage;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const result = await runWrite<{ ref: string; created: boolean }>(
      toast,
      'admin.networks.save',
      {
        ...(network ? { ref: network.ref } : {}),
        ...draft,
        homepageUrl: draft.homepageUrl || null,
        generatorKey: draft.generatorKey || null,
        clickRefParameter: draft.clickRefParameter || null,
        validationRegex: draft.validationRegex || null,
      },
      (r) => (r.created ? `${draft.name} created` : `${draft.name} saved`),
    );
    setSaving(false);
    if (result) onSaved(result.ref);
  };

  return (
    <Drawer title={network ? network.name : 'New network'} onClose={onClose}>
      <form onSubmit={submit}>
        <fieldset disabled={readOnly || saving} className="form-grid" style={{ border: 0, padding: 0, margin: 0 }}>
          <label>
            Name
            <input
              required
              minLength={2}
              value={draft.name}
              onChange={(e) => {
                const name = e.target.value;
                setDraft((d) => ({ ...d, name, slug: network || slugTouched ? d.slug : slugify(name) }));
              }}
            />
          </label>
          <label>
            Slug
            <input
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              value={draft.slug}
              onChange={(e) => {
                setSlugTouched(true);
                set('slug', e.target.value);
              }}
            />
          </label>
          <label className="span-2">
            Link builder (deeplink generator)
            <select value={draft.generatorKey} onChange={(e) => set('generatorKey', e.target.value)}>
              <option value="">None (links cannot be generated)</option>
              {data.generators.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Click-ref parameter
            <input className="mono" placeholder="e.g. clickref, sid, subId1" value={draft.clickRefParameter} onChange={(e) => set('clickRefParameter', e.target.value)} />
          </label>
          <label>
            Clickout adapter
            <select value={draft.clickoutAdapter} onChange={(e) => set('clickoutAdapter', e.target.value)}>
              {data.clickoutAdapters.map((a) => (
                <option key={a}>{a}</option>
              ))}
            </select>
          </label>
          <fieldset className="span-2 radio-row">
            <legend>Countries</legend>
            {data.countries.map((c) => (
              <label key={c.code} className="inline-check">
                <input
                  type="checkbox"
                  checked={draft.countryCodes.includes(c.code)}
                  onChange={(e) => set('countryCodes', e.target.checked ? [...draft.countryCodes, c.code] : draft.countryCodes.filter((x) => x !== c.code))}
                />
                {c.name}
              </label>
            ))}
          </fieldset>
          <label>
            Homepage
            <input type="url" value={draft.homepageUrl} onChange={(e) => set('homepageUrl', e.target.value)} />
          </label>
          <label>
            Status
            <select value={draft.status} onChange={(e) => set('status', e.target.value as typeof draft.status)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <label>
            Kind
            <select value={draft.kind} onChange={(e) => set('kind', e.target.value as typeof draft.kind)}>
              <option value="affiliate_network">Affiliate network</option>
              <option value="sub_affiliate_network">Sub-affiliate network</option>
            </select>
          </label>
          <label>
            Approvals
            <select value={draft.approvalLevel} onChange={(e) => set('approvalLevel', e.target.value as ApprovalLevel)}>
              {(Object.keys(APPROVAL_LEVEL_LABELS) as ApprovalLevel[]).map((level) => (
                <option key={level} value={level}>
                  {APPROVAL_LEVEL_LABELS[level]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Our account on partner sites
            <select value={draft.subAffiliatePolicy} onChange={(e) => set('subAffiliatePolicy', e.target.value as SubAffiliatePolicy)}>
              {(Object.keys(SUB_AFFILIATE_LABELS) as SubAffiliatePolicy[]).map((policy) => (
                <option key={policy} value={policy}>
                  {SUB_AFFILIATE_LABELS[policy]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Sub-ID pattern <small className="muted">optional</small>
            <span className="input-pair">
              <input type="checkbox" aria-label="Validate sub-IDs" checked={draft.validateSubid} onChange={(e) => set('validateSubid', e.target.checked)} />
              <input className="mono" value={draft.validationRegex} disabled={!draft.validateSubid} onChange={(e) => set('validationRegex', e.target.value)} />
            </span>
          </label>
        </fieldset>
        {readOnly ? <p className="muted">Only the platform operator’s owners and admins can change networks.</p> : null}
        {network ? (
          <p className="muted">
            {network.parameters} deeplink parameter(s) · <Link to={`/deeplinks?network=${network.ref}`}>Edit deeplinks</Link> · <Link to={`/programs?network=${network.ref}`}>{network.programs} program(s)</Link> ·{' '}
            <Link to={`/network-accounts?network=${network.ref}`}>{network.accounts} account(s)</Link>
          </p>
        ) : null}
        <div className="form-actions">
          {!readOnly ? (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {network ? 'Save' : 'Create network'}
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </form>
    </Drawer>
  );
}
